import {
  ErrorContent,
  Loading,
  NavigateButton,
  Table,
  Text,
} from '@/components';
import {
  TABLE_BUTTON_WIDTH,
  TABLE_DATETIME_WIDTH,
  datetimeFormat,
} from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useFilteredUsers } from '../_hooks/user';
import { UserName } from './UserName';

export const UsersTable: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageNumber = Number(searchParams.get('page') ?? '1');

  const users = useFilteredUsers({
    params: {
      filterBy: 'pagination',
      page: pageNumber,
      limit: 10,
      name: searchParams.get('name') ?? undefined,
      email: searchParams.get('email') ?? undefined,
    },
  });

  if (users.isError) {
    return <ErrorContent reloadable={false} />;
  }

  if (users.isPending) {
    return <Loading />;
  }

  return (
    <Table
      dataSource={users.data?.results}
      rowKey="id"
      columns={[
        {
          title: t('name'),
          dataIndex: 'name',
          render: (value, record) => (
            <UserName name={value} isIntegrated={record.isIntegrated} />
          ),
        },
        {
          title: t('email'),
          dataIndex: 'email',
          width: 400,
          render: (value) => <Text weight="regular" text={value} />,
        },
        {
          title: t('email-notification-for-comment-and-workflow'),
          dataIndex: 'isEmailNotificationEnabled',
          width: 170,
          render: (_, record) => (
            <Text
              weight="regular"
              text={
                record.isEmailNotificationEnabled
                  ? t('email-notification-target')
                  : t('email-notification-non-target')
              }
            />
          ),
        },
        {
          title: t('created-datetime'),
          dataIndex: 'createdAt',
          width: TABLE_DATETIME_WIDTH,
          render: (datetime) => (
            <Text weight="regular" text={datetimeFormat({ datetime })} />
          ),
        },
        {
          title: t('updated-datetime'),
          dataIndex: 'modifiedAt',
          width: TABLE_DATETIME_WIDTH,
          render: (datetime) => (
            <Text
              weight="regular"
              text={datetime ? datetimeFormat({ datetime }) : null}
            />
          ),
        },
        {
          title: '',
          key: 'actions',
          width: TABLE_BUTTON_WIDTH,
          render: (_, record) => (
            <NavigateButton
              type="default"
              path={`users/${record.id}`}
              text={t('details')}
              asLink
            />
          ),
        },
      ]}
      pagination={{
        pageSize: 10,
        position: ['bottomRight'],
        showSizeChanger: false,
        total: users.data?.count,
        current: pageNumber,
        onChange: (newPageNumber) => {
          searchParams.set('page', newPageNumber.toString());
          setSearchParams(searchParams);
        },
      }}
      data-dd-privacy="mask"
    />
  );
};
